import { TimePicker } from 'material-ui-pickers';
import moment from 'moment';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import ArrowDown from 'utils/images/arrow-down.svg';
import * as Yup from 'yup';
import { AVAILABILITY_DAYS, PATH_KEY_TO_POSITION } from '../../constants';
import useEditor from '../../hooks/use-editor';
import { capitalizeFirstLetter } from '../../utils';
import {
  requiredLabel,
  requiredNameWithMaxLength,
  requiredStatus,
} from '../../utils/validation';
import Alert from '../shared/Alert';
import Checkbox from '../shared/Checkbox';
import EditorButtons from '../shared/EditorButtons';
import EditorCard from '../shared/EditorCard';
import EditorCheckbox from '../shared/EditorCheckbox';
import EditorContainer from '../shared/EditorContainer';
import EditorForm from '../shared/EditorForm';
import EditorRow from '../shared/EditorRow';
import FieldRow from '../shared/FieldRow';
import NameField from '../shared/NameField';
import PrepAreaSelect from '../shared/PrepAreaSelect';
import StatusSelect from '../shared/StatusSelect';
import './index.scss';

const LEVEL = PATH_KEY_TO_POSITION['menuIdx'];

const checkedList = (items, index) =>
  items.map((item, i) => ({
    id: i,
    value: item,
    isChecked: index.has(item),
  }));

const Checkboxes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 2.1px;
`;
const EditorWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  top: 2px;
  position: relative;
`;
const HeaderWrapper = styled.div`
  margin-left: 5px
    ${props =>
      props.expanded &&
      css`
        margin-left: -1px;
      `};
`;
const EditorHeader = styled.h1`
  margin-left: 0px;
  font-family: 'Basier Circle';
  font-style: normal;
  font-weight: normal;
  line-height: 16px;
  letter-spacing: -0.2px;
  color: #000000;
  margin-bottom: 0px;
  margin-top: 8px;
  cursor: pointer;
`;
const ExpanderArrow = styled.div`
  width: 12px;
  height: 7px;
  margin-top: 2px;
  line-height: 12px;
  transform: rotate(270deg);
  cursor: pointer;
  margin-left: -2px;

  ${props => css`
    margin-top: ${props.level === 0 ? '10px' : '6px'};
  `}

  ${props =>
    props.expanded &&
    css`
      margin-top: -2px;
      transform: rotate(0deg);
      cursor: pointer;
    `}

  ${props =>
    props.hide &&
    css`
      visibility: hidden;
    `}
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 5px;
  padding-top: 16px;
  margin-left: 24px;
  cursor: pointer;

  ${props =>
    props.expanded &&
    css`
      margin-left: 0px;
      margin-top: 0px;
      cursor: default;
    `}
`;
const TimeWrapper = styled.div``;

const TimeText = styled.p`
  font-family: 'Basier Circle';
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 13px;
  text-align: right;
  letter-spacing: -0.2px;
  color: #797979;
  padding-top: 4px;
  cursor: default;
`;

const Divider = styled.hr`
  width: 165px;
  height: 0px;
  border: 0.2px solid #dedede;
  margin: 0;
`;
const CheckboxDivider = styled.hr`
  width: 125px;
  height: 0px;
  border: 0.2px solid #dedede;
  margin-top: 0;
  margin-bottom: 0;
  margin-left: 20px;
`;

const validation = Yup.object({
  name: requiredNameWithMaxLength,
  status: requiredStatus,
  label: requiredLabel,
});

function MenuEditor(props) {
  const { onCancel, menu, menuIdx, setShowEditor } = props;

  const [showExpandedMenuEditor, setShowExpandedMenuEditor] = useState(false);
  const toggleMenuEditor = event => {
    if (
      [
        'editor',
        'editor-wrapper',
        'expander-arrow',
        'header-wrapper',
        'header',
      ].includes(event.target.id)
    ) {
      setShowExpandedMenuEditor(!showExpandedMenuEditor);
    }
  };

  let createMode = true;
  if (menu) {
    createMode = false;
  }

  const {
    item,
    error,
    loading,
    updateHandler,
    submitHandler,
    deleteHandler,
    onFieldChange,
  } = useEditor({
    init: menu,
    validation,
    type: 'menu',
    path: [{ key: 'menuIdx', value: menuIdx }],
    setShowEditor,
  });

  const onLabelChange = onFieldChange('label');
  const onStatusChange = onFieldChange('status');
  const onClientOnlyChange = onFieldChange('clientOnly');
  const onNameChange = onFieldChange('name');

  const onTimesChange = onFieldChange('openingTimes');

  const daysOfTheWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  if (!item.openingTimes) {
    item.openingTimes = {
      monday: { open: '0000', closed: '0000' },
      tuesday: { open: '0000', closed: '0000' },
      wednesday: { open: '0000', closed: '0000' },
      thursday: { open: '0000', closed: '0000' },
      friday: { open: '0000', closed: '0000' },
      saturday: { open: '0000', closed: '0000' },
      sunday: { open: '0000', closed: '0000' },
    };
  }

  const openingTimesInitial = { ...item.openingTimes };
  daysOfTheWeek.forEach(day => {
    if (!openingTimesInitial[day]) {
      openingTimesInitial[day] = { open: '0000', closed: '0000' };
    }
  });

  const [availableTimes, setavailableTimes] = useState(openingTimesInitial);

  const availableDaysList = checkedList(
    AVAILABILITY_DAYS,
    new Set(
      Object.keys(item.openingTimes).filter(day => !!item.openingTimes[day])
    )
  );

  const handleAvailabilityDaysChange = (value, checked) => {
    const openingTimes = { ...item.openingTimes };

    if (checked) {
      openingTimes[value] = { open: '0000', closed: '0000' };
    } else {
      delete openingTimes[value];
    }

    item.openingTimes = openingTimes;
    onTimesChange({ target: { value: openingTimes } });
  };

  const handleTimeChange = (day, oc, newMmt) => {
    const ot = { ...item.openingTimes };
    const outputStr = newMmt.format('HHmm');

    ot[day][oc] = outputStr;

    onTimesChange({ target: { value: ot } });
    item.openingTimes = ot;

    let availableTimesForDay = availableTimes[day];
    availableTimesForDay = { ...availableTimesForDay, [oc]: outputStr };
    setavailableTimes({ ...availableTimes, [day]: availableTimesForDay });
  };

  return (
    <EditorContainer level={LEVEL}>
      <EditorForm>
        <NameField
          id={`menu-name-${item.id}`}
          type="menu"
          value={item.name || ''}
          placeholder="e.g. Food, Drinks or Desserts"
          onChange={onNameChange}
        />

        <EditorRow>
          <FieldRow>
            <PrepAreaSelect
              margin="0px"
              id={`prep-select-${item.id}`}
              value={item.label}
              onChange={onLabelChange}
            />

            <StatusSelect
              id={`status-select-${item.id}`}
              value={item.status}
              onChange={onStatusChange}
            />

            <Checkbox
              style={{ height: 35 }}
              onSelect={(e, checked) =>
                onClientOnlyChange({ target: { value: checked } })
              }
              value={item.clientOnly || false}
              label="Only for clients"
              id={`client-check-${item.id}`}
              noMargin={true}
              isChecked={item.clientOnly || false}
            />
          </FieldRow>
        </EditorRow>

        <EditorCard
          expanded={showExpandedMenuEditor}
          onClick={toggleMenuEditor}
          id="editor"
        >
          <Wrapper id="editor-wrapper" expanded={showExpandedMenuEditor}>
            <ExpanderArrow expanded={showExpandedMenuEditor}>
              <img alt="expand arrow" src={ArrowDown} id="expander-arrow" />
            </ExpanderArrow>
            <HeaderWrapper
              id="header-wrapper"
              expanded={showExpandedMenuEditor}
            >
              <EditorHeader
                id="header"
                style={{
                  fontSize: '15px',
                  marginLeft: '10px',
                  marginBottom: '1px',
                }}
              >
                Availability
              </EditorHeader>
            </HeaderWrapper>
          </Wrapper>
          {showExpandedMenuEditor && (
            <EditorWrapper>
              <Checkboxes id={`${item.id}-days-checklist`}>
                {availableDaysList.map((d, index) => (
                  <div>
                    <EditorCheckbox
                      onSelect={handleAvailabilityDaysChange}
                      value={d.value}
                      label={capitalizeFirstLetter(d.value)}
                      id={d.id}
                      key={`availability-check-${d.id}`}
                      isChecked={d.isChecked}
                    />
                    {!(availableDaysList.length - 1 === index) && (
                      <CheckboxDivider />
                    )}
                  </div>
                ))}
              </Checkboxes>
              <div className="menu_editor_time_wrapper">
                {availableDaysList.map((d, index) => {
                  const openTime = moment(availableTimes[d.value].open, 'HHmm');
                  const closeTime = moment(
                    availableTimes[d.value].closed,
                    'HHmm'
                  );
                  return (
                    <TimeWrapper>
                      <div
                        key={`d-time-pick-${d.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          height: '38px',
                          marginTop: '-1px',
                          marginLeft: '2px',
                        }}
                      >
                        <TimePicker
                          onChange={e => handleTimeChange(d.value, 'open', e)}
                          format="hh:mm a"
                          value={openTime}
                          className={`menu__time__picker__input ${
                            d.isChecked ? '' : 'time-picker-no-underline'
                          }`}
                          selected={d.isChecked}
                          disabled={!d.isChecked}
                        />{' '}
                        <TimeText>until{'  '}</TimeText>
                        <TimePicker
                          onChange={e => handleTimeChange(d.value, 'closed', e)}
                          format="hh:mm a"
                          value={closeTime}
                          className={`menu__time__picker__input ${
                            d.isChecked ? '' : 'time-picker-no-underline'
                          }`}
                          selected={d.isChecked}
                          disabled={!d.isChecked}
                        />
                      </div>
                      {!(availableDaysList.length - 1 === index) && <Divider />}
                    </TimeWrapper>
                  );
                })}
              </div>
            </EditorWrapper>
          )}
        </EditorCard>
        <EditorButtons
          type="menu"
          create={createMode}
          loading={loading}
          onCancel={onCancel}
          onSubmit={submitHandler}
          onUpdate={updateHandler}
          onDelete={deleteHandler}
        />
      </EditorForm>
      {error ? <Alert>{error}</Alert> : null}
    </EditorContainer>
  );
}

export default MenuEditor;



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/MenuEditor/components/MenuList/menu-editor.jsx